import React, { FC, Fragment } from 'react';

import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';

import { focusInput } from '@components/utils';

import { DraggableField } from './DraggableField';
import { deleteField, fieldBuilder, fieldOptionsBuilder, updateField } from './utils';

interface Props {
  options: string[];
  onChange?: (v: string[]) => void;
  onBlur?: (v: string[]) => void;
  placeholder?: string;
  type: string;
  onPaste?: (args: { e: React.ClipboardEvent<HTMLInputElement>; position: number }) => void;
  shouldValidate?: boolean;
}

export const DraggableFields: FC<Props> = ({
  shouldValidate,
  type,
  onChange,
  onBlur,
  options,
  placeholder,
  onPaste
}) => {
  const fieldOptions = options ?? [''];

  const fields: FieldData[] = fieldBuilder(fieldOptions);

  const handleFieldUpdate = (field: FieldData) => {
    onChange?.(updateField(fieldOptions, field));
  };

  const handleFieldBlur = (field: FieldData) => {
    onBlur?.(updateField(fieldOptions, field));
  };

  const handleInputDelete = (field: FieldData) => {
    onChange?.(deleteField(fieldOptions, field));
  };

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    const {
      destination: { index: destinationIndex },
      source: { index: sourceIndex }
    } = result;

    const newFields = fields.reduce((acc, { position, ...rest }) => {
      if (position === destinationIndex) {
        return [...acc, { ...rest, position: sourceIndex }];
      }

      if (position === sourceIndex) {
        return [...acc, { ...rest, position: destinationIndex }];
      }

      return [...acc, { ...rest, position }];
    }, []);

    onChange?.(fieldOptionsBuilder(newFields));
  };

  const handleOnEnterKeyPress = (field: FieldData) => {
    if (!field.label) {
      return;
    }

    onChange?.([...fieldOptions, '']);
    focusInput(`field-${type}-${fieldOptions.length}`);
  };

  return (
    <DragDropContext onDragEnd={handleOnDragEnd}>
      <Droppable droppableId='fields'>
        {(dropProvided) => (
          <section ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
            {fields.map((field) => (
              <Fragment key={field.position}>
                <Draggable
                  draggableId={`field_draggable-${field.position}`}
                  index={field.position}
                  isDragDisabled={fields.length === 1}
                >
                  {(dragProvided, snapshot) => (
                    <DraggableField
                      dragProvided={dragProvided}
                      isDragging={snapshot.isDragging}
                      field={field}
                      shouldValidate={shouldValidate}
                      fields={fields}
                      type={type}
                      placeholder={placeholder}
                      onPaste={onPaste}
                      onChange={handleFieldUpdate}
                      onBlur={handleFieldBlur}
                      onDelete={handleInputDelete}
                      hideControls={fields.length === 1}
                      onEnterKeyPress={() => handleOnEnterKeyPress(field)}
                    />
                  )}
                </Draggable>
              </Fragment>
            ))}
            {dropProvided.placeholder}
          </section>
        )}
      </Droppable>
    </DragDropContext>
  );
};
